import { type ReactNode } from 'react';
import styled from 'types/theme.types';
import { css } from '@emotion/react';
import { legacyOnly, newDesignOnly } from 'constants/themes.constants';
import { BaseItemContainer } from 'components/layout/navbar/navbar-panel.utils';
import { useNavbarCollapse } from 'components/layout/navbar/navbar-collapse-context';

interface NavbarHeaderProps {
  menuTitle: string;
  menuIcon: ReactNode;
}

const NavbarHeader = ({ menuTitle, menuIcon }: NavbarHeaderProps) => {
  const { isCollapsed } = useNavbarCollapse();
  return (
    <NavbarHeaderContainer>
      <BaseItemContainer>
        <IconContainer>{menuIcon}</IconContainer>
        {!isCollapsed && <NavbarTitle>{menuTitle}</NavbarTitle>}
      </BaseItemContainer>
    </NavbarHeaderContainer>
  );
};

/**
 * Keeps the rail header level with the breadcrumb strip. Off the theme rather than the module constant so
 * both sides read one number: `theme.layout.breadcrumbBarHeight` is the same 35px for legacy and Figma's
 * measured 38 for the new design (`themes.constants.ts`), so this needs no branch. Coordination point with
 * C2/C3, which own the rail.
 */
const NavbarHeaderContainer = styled.div`
  height: ${({ theme }) => theme.layout.breadcrumbBarHeight};
`;

const IconContainer = styled.div`
  * {
    ${legacyOnly(
      theme => css`
        color: ${theme.lightGray};
      `
    )}
    ${newDesignOnly(css`
      color: var(--text-heading-on-dark);
    `)}
  }
`;

const NavbarTitle = styled.div`
  ${legacyOnly(css`
    font-weight: bold;
  `)}
  /* Figma's title is UI & Labels / Label Medium: 14px regular. */
  ${newDesignOnly(css`
    font-weight: var(--font-weight-regular);
    line-height: var(--line-height-labels-label-md);
  `)}
  font-size: 14px;
  ${legacyOnly(
    theme => css`
      color: ${theme.lightGray};
    `
  )}
  ${newDesignOnly(css`
    color: var(--text-heading-on-dark);
  `)}
`;

export default NavbarHeader;
